import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment/environment';
import { type Observable, of } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';

/**
 * The master catalogs. Re-exported from `@libs/dtos` instead of retyped: this
 * list was a hand-kept copy and it went stale the day the vacancy-rules matrix
 * added nine more (12-ago), leaving the admin screen unable to edit catalogs
 * the backend was already serving.
 */
export { CATALOG_TYPES, type CatalogType } from '@libs/dtos/merit/config.dto';

import type {
	CompanyScoringBlockDTO,
	CompanyScoringConfigDTO,
	CompanyScoringMapsDTO,
	UpdateCompanyScoringDTO,
} from '@libs/dtos/merit/company-scoring.dto';
import type { CatalogType } from '@libs/dtos/merit/config.dto';
import { SessionCacheRegistry } from '../shared/session-cache.registry';

export interface CatalogItem {
	id: number;
	type: CatalogType;
	label: string;
	slug: string;
	order: number;
	active: boolean;
	metadata: Record<string, unknown> | null;
	/**
	 * Solo en `positions`: el nivel del puesto (Jumeirah 13-ago). `null` = nadie
	 * lo ha clasificado todavía, y entonces la vacante espera a Kairos para
	 * poder publicarse.
	 */
	seniorityTier: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface CatalogItemPayload {
	label: string;
	slug?: string;
	order?: number;
	active?: boolean;
	/** Solo en `positions`. `null` = sin clasificar (Jumeirah 13-ago). */
	seniorityTier?: string | null;
	/**
	 * Los extras por tipo. Hoy lo usan los puestos, para decir a qué departamento
	 * pertenecen (`{ department: '<slug>' }`, 12/09), que es lo que anida los dos
	 * desplegables del formulario de vacante.
	 *
	 * Se manda ENTERO: el PATCH reemplaza el objeto, no lo fusiona, así que quien
	 * escriba una clave tiene que partir de las que ya había.
	 */
	metadata?: Record<string, unknown> | null;
}

export interface KScoreDimension {
	id: number;
	key: string;
	label: string;
	weight: number;
	order: number;
}

/**
 * El baremo de EMPRESA. Los tipos se importan del contrato en vez de copiarse:
 * la lista de dimensiones del K-Score ya se quedó desfasada una vez por tenerla
 * a mano aquí, y este contrato tiene siete mapas anidados.
 */
export type CompanyScoringBlock = CompanyScoringBlockDTO;
export type CompanyScoringMaps = CompanyScoringMapsDTO;
export type CompanyScoringConfig = CompanyScoringConfigDTO;
export type CompanyScoringPayload = UpdateCompanyScoringDTO;

/** One recorded weight change (M00 §3 — every edit is logged, feeds M13). */
export interface KScoreWeightChange {
	id: number;
	dimensionKey: string;
	previousWeight: number;
	newWeight: number;
	changedAt: string;
	changedBy: { id: number; name: string; lastName: string; email: string } | null;
}

/** `validation_rules` section of the global settings (M00 §2). */
export interface ValidationRulesSettings {
	candidateSlaHours: number;
	companySlaHours: number;
	commentRequiredOnRejection: boolean;
	commentRequiredOnObservations: boolean;
	luxuryReadyThreshold: number;
	highPotentialThreshold: number;
}

export interface Window72hSettings {
	enabled: boolean;
	durationHours: number;
	minKScore: number;
	plansWithAccess: string[];
}

/**
 * `talent_access` · reglas de acceso al pool de talento.
 *
 * `advancedFilterPlans` son los planes que pueden filtrar por nivel de lujo y
 * estado legal. Es un ajuste y no una constante porque la clienta lo mueve: el
 * servidor lo impone sobre la consulta y el rail solo lo obedece.
 */
export interface TalentAccessSettings {
	requireActiveVacancy: boolean;
	advancedFilterPlans: string[];
}

export interface IntegrationsSettings {
	crmEnabled: boolean;
	crmProvider: string | null;
	emailProvider: string | null;
	invitationEmailEnabled: boolean;
}

export interface SecuritySettings {
	mfaRequiredForAdmins: boolean;
	sessionTimeoutMinutes: number;
	passwordMinLength: number;
}

/** T4 · interruptor de «puestos cercanos» del matching. */
export interface MatchingSettings {
	adjacentEnabled: boolean;
}

export interface MeritSettings {
	validation_rules: ValidationRulesSettings;
	window_72h: Window72hSettings;
	talent_access: TalentAccessSettings;
	integrations: IntegrationsSettings;
	security: SecuritySettings;
	matching: MatchingSettings;
}

/** PATCH body: a partial map of section → partial value. */
export type MeritSettingsPatch = {
	[K in keyof MeritSettings]?: Partial<MeritSettings[K]>;
};

export type BillingMode = 'monthly' | 'annual';
export const BILLING_MODES: BillingMode[] = ['monthly', 'annual'];

/** Price of a plan in one billing mode. `priceAed: null` = quoted (ENTERPRISE). */
export interface PlanPricing {
	priceAed: number | null;
	cycleDays: number | null;
	durationMonths: number | null;
	stripePriceId: string | null;
}

/**
 * What a plan grants in one billing mode.
 *
 * `null` is UNLIMITED, never zero — an empty Video Requests box is PRIME's
 * "unlimited (fair use)", while a typed 0 is ACCESS's "not included". The
 * editor has to keep the two apart or it silently rewrites the commercial model.
 */
export interface PlanEntitlements {
	vacancyCredits: number | null;
	activeVacancies: number | null;
	contactUnlocks: number | null;
	invitations: number | null;
	videoRequests: number | null;
	shortlists: number | null;
	shortlistCandidates: number;
	seats: number | null;
	venues: number | null;
	priority: 'first' | 'priority' | 'standard';
	priorityDelayHours: number;
	replacementDays: number;
	/** PTD por categoría para este plan y modalidad (04/09). Null = lista general. */
	ptdOperationalAed?: number | null;
	ptdSupervisorAed?: number | null;
	ptdManagementAed?: number | null;
	ptdSeniorDirectorAed?: number | null;
}

/** Ceilings past which the account has to move to ENTERPRISE. */
export interface PlanCaps {
	vacancies: number | null;
	contactUnlocks: number | null;
	invitations: number | null;
	shortlists: number | null;
	seats: number | null;
	venues: number | null;
}

export interface Plan {
	key: string;
	name: string;
	priceAed: number;
	recommended: boolean;
	activeRoles: number | null;
	annualRoles: number;
	videosPerMonth: number | null;
	replacementDays: number;
	earlyAccessWindow: boolean;
	order: number;
	active: boolean;
	isEnterprise: boolean;
	contactSales: boolean;
	/** Monthly and Annual are two products, each with its own numbers. */
	pricing: Record<BillingMode, PlanPricing | null>;
	entitlements: Record<BillingMode, PlanEntitlements | null>;
	caps: Record<BillingMode, PlanCaps | null>;
}

export interface Addon {
	key: string;
	name: string;
	description: string | null;
	priceAed: number;
	order: number;
	active: boolean;
	/** Plan it belongs to; `null` = sold on every plan. */
	planKey: string | null;
	/** What buying it grants; `null` = nothing metered. */
	effect: string | null;
	quantity: number;
	priceAnnualAed: number | null;
	cycleLimit: number | null;
	annualLimit: number | null;
}

/** One recorded move of a price or an entitlement. */
export interface PlanChange {
	id: number;
	planKey: string;
	mode: string | null;
	field: string;
	oldValue: string | null;
	newValue: string | null;
	createdAt: string;
}

export interface AdminRole {
	id: number;
	name: string;
	description: string | null;
	permissions: string[];
}

/**
 * One row of the permission table, as the internal-roles editor needs it: the
 * `description` is the human-readable label (Spanish, straight from the seed)
 * and `resource` is what the editor groups by.
 */
export interface PermissionCatalogItem {
	name: string;
	resource: string;
	action: string;
	description: string | null;
}

/**
 * M00 · master catalogs and configuration.
 *
 * Reads of catalogs / plans / add-ons are public on the API; writes need the
 * matching permission (`catalog:write`, `kscore:weights`, `plan:write`,
 * `merit-settings:write`), which the backend enforces.
 */
@Injectable({ providedIn: 'root' })
export class MeritConfigService {
	private readonly sessionCaches = inject(SessionCacheRegistry);

	constructor() {
		// Cerrar sesión no recarga la página, así que esta caché sobreviviría
		// al cambio de cuenta si no se vaciara aquí.
		this.sessionCaches.register(() => {
			this.invalidateCatalogs();
			this.invalidatePlans();
		});
	}

	private readonly http = inject(HttpClient);
	private readonly baseUrl = `${environment.API_URL}/config`;

	// ---- Read caches --------------------------------------------------------
	// Catalogs, settings and plans are near-static admin-edited data, yet every
	// screen used to refetch them on mount (the 7 master catalogs on each form,
	// the settings blob once per config tab). One shareReplay per resource keeps
	// a single in-flight/last answer; the WRITE methods below invalidate it.

	private readonly catalogCache = new Map<string, Observable<CatalogItem[]>>();
	private readonly allCatalogsCache = new Map<string, Observable<Record<CatalogType, CatalogItem[]>>>();
	private settingsCache: Observable<MeritSettings> | null = null;
	private readonly plansCache = new Map<string, Observable<Plan[]>>();

	private invalidateCatalogs(): void {
		this.catalogCache.clear();
		this.allCatalogsCache.clear();
	}

	private invalidatePlans(): void {
		this.plansCache.clear();
	}

	// ---- Catalogs ----------------------------------------------------------

	/** Values of one catalog. The admin CRUD asks for the inactive ones too. */
	listCatalog(type: CatalogType, includeInactive = false): Observable<CatalogItem[]> {
		const cacheKey = `${type}:${includeInactive}`;
		let cached = this.catalogCache.get(cacheKey);
		if (!cached) {
			const params = includeInactive ? new HttpParams().set('includeInactive', 'true') : undefined;
			cached = this.http.get<CatalogItem[]>(`${this.baseUrl}/catalogs/${type}`, { params }).pipe(
				// A failed fetch must not be cached as the answer forever.
				tap({ error: () => this.catalogCache.delete(cacheKey) }),
				shareReplay({ bufferSize: 1, refCount: false }),
			);
			this.catalogCache.set(cacheKey, cached);
		}
		return cached;
	}

	/** Every catalog at once, keyed by type. */
	listAllCatalogs(includeInactive = false): Observable<Record<CatalogType, CatalogItem[]>> {
		const cacheKey = String(includeInactive);
		let cached = this.allCatalogsCache.get(cacheKey);
		if (!cached) {
			const params = includeInactive ? new HttpParams().set('includeInactive', 'true') : undefined;
			cached = this.http
				.get<Record<CatalogType, CatalogItem[]>>(`${this.baseUrl}/catalogs`, { params })
				.pipe(
					tap({ error: () => this.allCatalogsCache.delete(cacheKey) }),
					shareReplay({ bufferSize: 1, refCount: false }),
				);
			this.allCatalogsCache.set(cacheKey, cached);
		}
		return cached;
	}

	createCatalogItem(type: CatalogType, payload: CatalogItemPayload): Observable<CatalogItem> {
		return this.http
			.post<CatalogItem>(`${this.baseUrl}/catalogs/${type}`, payload)
			.pipe(tap(() => this.invalidateCatalogs()));
	}

	updateCatalogItem(type: CatalogType, id: number, payload: Partial<CatalogItemPayload>): Observable<CatalogItem> {
		return this.http
			.patch<CatalogItem>(`${this.baseUrl}/catalogs/${type}/${id}`, payload)
			.pipe(tap(() => this.invalidateCatalogs()));
	}

	/**
	 * Deactivates the value — the API never deletes it, so records that already
	 * reference it keep resolving (M00 §3).
	 */
	deactivateCatalogItem(type: CatalogType, id: number): Observable<CatalogItem> {
		return this.http
			.delete<CatalogItem>(`${this.baseUrl}/catalogs/${type}/${id}`)
			.pipe(tap(() => this.invalidateCatalogs()));
	}

	/** Reactivating is a plain update: there is no dedicated endpoint. */
	reactivateCatalogItem(type: CatalogType, id: number): Observable<CatalogItem> {
		return this.updateCatalogItem(type, id, { active: true });
	}

	// ---- K-Score, plans, add-ons, roles, settings ---------------------------

	getKScore(): Observable<KScoreDimension[]> {
		return this.http.get<KScoreDimension[]>(`${this.baseUrl}/kscore`);
	}

	updateKScore(dimensions: { key: string; weight: number }[]): Observable<KScoreDimension[]> {
		return this.http.patch<KScoreDimension[]>(`${this.baseUrl}/kscore`, { dimensions });
	}

	/**
	 * El baremo de empresa vigente: criterios, pesos y los siete mapas.
	 *
	 * Sin caché a propósito — publicar sube de versión, y la pantalla tiene que
	 * enseñar la versión que acaba de quedar vigente, no la que leyó al entrar.
	 */
	getCompanyScoring(): Observable<CompanyScoringConfig> {
		return this.http.get<CompanyScoringConfig>(`${this.baseUrl}/company-scoring`);
	}

	/**
	 * Publica un baremo de empresa nuevo. Necesita `kscore:weights`.
	 *
	 * NO recalcula las empresas ya puntuadas: cada puntuación guardada apunta al
	 * modelo que la produjo, y volver a puntuar la cartera es una acción aparte.
	 */
	updateCompanyScoring(payload: CompanyScoringPayload): Observable<CompanyScoringConfig> {
		return this.http.patch<CompanyScoringConfig>(`${this.baseUrl}/company-scoring`, payload);
	}

	/** Weight change log, newest first. Read-only audit trail (M13). */
	getKScoreHistory(limit = 50): Observable<KScoreWeightChange[]> {
		const params = new HttpParams().set('limit', String(limit));
		return this.http.get<KScoreWeightChange[]>(`${this.baseUrl}/kscore/history`, { params });
	}

	/** Every settings section at once; missing rows come back as the defaults. */
	getSettings(): Observable<MeritSettings> {
		if (!this.settingsCache) {
			this.settingsCache = this.http.get<MeritSettings>(`${this.baseUrl}/settings`).pipe(
				tap({
					error: () => {
						this.settingsCache = null;
					},
				}),
				shareReplay({ bufferSize: 1, refCount: false }),
			);
		}
		return this.settingsCache;
	}

	/** Partial update, one or more sections. Each is validated server-side. */
	updateSettings(patch: MeritSettingsPatch): Observable<MeritSettings> {
		// El PATCH devuelve el blob entero: ES la caché fresca. Tirarla obligaba
		// al siguiente que entrara en cualquiera de los cuatro tabs a repetir el
		// GET, teniendo ya la respuesta en la mano.
		return this.http.patch<MeritSettings>(`${this.baseUrl}/settings`, patch).pipe(
			tap((fresh) => {
				this.settingsCache = of(fresh).pipe(shareReplay({ bufferSize: 1, refCount: false }));
			}),
		);
	}

	listPlans(includeInactive = false): Observable<Plan[]> {
		const cacheKey = String(includeInactive);
		let cached = this.plansCache.get(cacheKey);
		if (!cached) {
			const params = includeInactive ? new HttpParams().set('includeInactive', 'true') : undefined;
			cached = this.http
				.get<Plan[]>(`${this.baseUrl}/plans`, { params })
				.pipe(
					tap({ error: () => this.plansCache.delete(cacheKey) }),
					shareReplay({ bufferSize: 1, refCount: false }),
				);
			this.plansCache.set(cacheKey, cached);
		}
		return cached;
	}

	listAddons(includeInactive = false): Observable<Addon[]> {
		const params = includeInactive ? new HttpParams().set('includeInactive', 'true') : undefined;
		return this.http.get<Addon[]>(`${this.baseUrl}/addons`, { params });
	}

	/** Identity of a plan (name, order, active, Contact Sales). Needs `plan:write`. */
	updatePlan(key: string, patch: Partial<Plan>): Observable<Plan> {
		return this.http.patch<Plan>(`${this.baseUrl}/plans/${key}`, patch).pipe(tap(() => this.invalidatePlans()));
	}

	updatePlanPricing(key: string, mode: BillingMode, patch: Partial<PlanPricing>): Observable<PlanPricing> {
		return this.http
			.patch<PlanPricing>(`${this.baseUrl}/plans/${key}/pricing/${mode}`, patch)
			.pipe(tap(() => this.invalidatePlans()));
	}

	updatePlanEntitlements(
		key: string,
		mode: BillingMode,
		patch: Partial<PlanEntitlements>,
	): Observable<PlanEntitlements> {
		return this.http
			.patch<PlanEntitlements>(`${this.baseUrl}/plans/${key}/entitlements/${mode}`, patch)
			.pipe(tap(() => this.invalidatePlans()));
	}

	/** Clearing every field removes the ceiling, and the API answers `null`. */
	updatePlanCaps(key: string, mode: BillingMode, patch: Partial<PlanCaps>): Observable<PlanCaps | null> {
		return this.http
			.patch<PlanCaps | null>(`${this.baseUrl}/plans/${key}/caps/${mode}`, patch)
			.pipe(tap(() => this.invalidatePlans()));
	}

	updateAddon(key: string, patch: Partial<Addon>): Observable<Addon> {
		return this.http.patch<Addon>(`${this.baseUrl}/addons/${key}`, patch);
	}

	/** What this plan was sold at before today. Newest first. */
	planHistory(key: string): Observable<PlanChange[]> {
		return this.http.get<PlanChange[]>(`${this.baseUrl}/plans/${key}/history`);
	}

	/** The four internal roles (M00 §2), in ladder order. Never created nor deleted. */
	listAdminRoles(): Observable<AdminRole[]> {
		return this.http.get<AdminRole[]>(`${this.baseUrl}/admin-roles`);
	}

	/** Every permission the system knows about — the source of the role editor. */
	listPermissionsCatalog(): Observable<PermissionCatalogItem[]> {
		return this.http.get<PermissionCatalogItem[]>(`${this.baseUrl}/permissions-catalog`);
	}

	/**
	 * Replaces the role's permission set with `permissions` (the full list, not a
	 * delta). Needs `admin-role:write`, which the backend enforces.
	 */
	updateAdminRolePermissions(name: string, permissions: string[]): Observable<AdminRole> {
		return this.http.patch<AdminRole>(`${this.baseUrl}/admin-roles/${name}`, { permissions });
	}
}
